import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DeductionCalculators } from "@/components/DeductionCalculators";

const STATE_OPTIONS = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" }
];

const MyDeductions = () => {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("recommended");
  
  // Business Profile State
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [profileForm, setProfileForm] = useState({
    business_type: "general",
    annual_revenue: "",
    has_employees: false,
    employee_count: 0,
    has_home_office: false,
    has_vehicle: false,
    vehicle_usage_percent: 50,
    has_health_insurance: false,
    has_retirement_plan: false,
    travel_frequency: "none",
    entertainment_expenses: false,
    uses_contractors: false,
    has_inventory: false,
    state: "CA"
  });
  
  // Personalized Deductions State
  const [recommendations, setRecommendations] = useState({ highly_relevant: [], other_deductions: [] });
  const [searchQuery, setSearchQuery] = useState("");
  
  // User Tracked Deductions State
  const [trackedDeductions, setTrackedDeductions] = useState([]);
  const [deductionsSummary, setDeductionsSummary] = useState({});
  
  // Tax Savings State
  const [taxSavings, setTaxSavings] = useState(null);
  
  // Custom Deduction Dialog
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customDeduction, setCustomDeduction] = useState({
    name: "",
    category: "Miscellaneous",
    description: "",
    estimated_amount: "",
    notes: ""
  });
  
  // Amount Entry Dialog
  const [showAmountDialog, setShowAmountDialog] = useState(false);
  const [selectedDeductionForAmount, setSelectedDeductionForAmount] = useState(null);
  const [amountForm, setAmountForm] = useState({ estimated: "", actual: "", notes: "" });

  const categories = [
    "Office & Workspace", "Technology & Equipment", "Marketing & Advertising",
    "Professional Services", "Travel & Transportation", "Meals & Entertainment",
    "Education & Training", "Insurance", "Banking & Finance", "Taxes & Government Fees",
    "Employee & Contractor Costs", "Shipping & Delivery", "Inventory & Cost of Goods",
    "Depreciation & Amortization", "Miscellaneous"
  ];

  const fetchData = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [profileRes, typesRes, recommendationsRes, trackedRes, savingsRes] = await Promise.all([
        api.get(`/business-profile/${selectedBusiness.id}`),
        api.get("/deductions/business-types"),
        api.get(`/deductions/personalized/${selectedBusiness.id}`),
        api.get(`/user-deductions/${selectedBusiness.id}`),
        api.get(`/deductions/tax-savings/${selectedBusiness.id}`)
      ]);
      
      setProfile(profileRes.data.profile);
      setHasProfile(profileRes.data.has_profile);
      if (profileRes.data.profile) {
        setProfileForm({
          ...profileForm,
          ...profileRes.data.profile,
          annual_revenue: profileRes.data.profile.annual_revenue || ""
        });
      }
      
      setBusinessTypes(typesRes.data);
      setRecommendations(recommendationsRes.data);
      setTrackedDeductions(trackedRes.data.deductions);
      setDeductionsSummary(trackedRes.data.summary);
      setTaxSavings(savingsRes.data);
    } catch (error) {
      console.error("Failed to fetch deductions data:", error);
      toast.error("Failed to load deductions data");
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveProfile = async () => {
    try {
      await api.post("/business-profile", {
        business_id: selectedBusiness.id,
        ...profileForm,
        annual_revenue: profileForm.annual_revenue ? parseFloat(profileForm.annual_revenue) : null
      });
      toast.success("Business profile saved!");
      setShowProfileDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save profile");
    }
  };

  const addDeductionToTracking = async (deduction) => {
    setSelectedDeductionForAmount(deduction);
    setAmountForm({ estimated: "", actual: "", notes: "" });
    setShowAmountDialog(true);
  };

  const confirmAddDeduction = async () => {
    if (!selectedDeductionForAmount) return;
    try {
      await api.post("/user-deductions", {
        business_id: selectedBusiness.id,
        deduction_id: selectedDeductionForAmount.id,
        name: selectedDeductionForAmount.name,
        category: selectedDeductionForAmount.category,
        description: selectedDeductionForAmount.description,
        estimated_amount: parseFloat(amountForm.estimated) || 0,
        actual_amount: parseFloat(amountForm.actual) || 0,
        notes: amountForm.notes,
        is_custom: false,
        tax_year: 2024
      });
      toast.success(`Added "${selectedDeductionForAmount.name}" to your deductions`);
      setShowAmountDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add deduction");
    }
  };

  const addCustomDeduction = async () => {
    if (!customDeduction.name) {
      toast.error("Please enter a deduction name");
      return;
    }
    try {
      await api.post("/user-deductions", {
        business_id: selectedBusiness.id,
        deduction_id: null,
        name: customDeduction.name,
        category: customDeduction.category,
        description: customDeduction.description || "Custom deduction",
        estimated_amount: parseFloat(customDeduction.estimated_amount) || 0,
        actual_amount: 0,
        notes: customDeduction.notes,
        is_custom: true,
        tax_year: 2024
      });
      toast.success("Custom deduction added!");
      setShowCustomDialog(false);
      setCustomDeduction({ name: "", category: "Miscellaneous", description: "", estimated_amount: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add custom deduction");
    }
  };

  const updateTrackedDeduction = async (deductionId, updates) => {
    try {
      await api.put(`/user-deductions/${deductionId}`, updates);
      toast.success("Deduction updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update deduction");
    }
  };

  const deleteTrackedDeduction = async (deductionId) => {
    try {
      await api.delete(`/user-deductions/${deductionId}`);
      toast.success("Deduction removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete deduction");
    }
  };

  // Handler for calculator-based deductions
  const addCalculatorDeduction = async (calculatedDeduction) => {
    try {
      await api.post("/user-deductions", {
        business_id: selectedBusiness.id,
        deduction_id: null,
        name: calculatedDeduction.name,
        category: calculatedDeduction.category,
        description: calculatedDeduction.description,
        estimated_amount: calculatedDeduction.estimated_amount,
        actual_amount: 0,
        notes: calculatedDeduction.calculation_method ? `Calculated using ${calculatedDeduction.calculation_method} method` : null,
        is_custom: true,
        tax_year: 2024
      });
      toast.success(`Added "${calculatedDeduction.name}" ($${calculatedDeduction.estimated_amount.toLocaleString()}) to your deductions`);
      fetchData();
    } catch (error) {
      toast.error("Failed to add calculated deduction");
    }
  };

  const filteredRecommendations = recommendations.highly_relevant.filter(d => 
    !searchQuery || 
    d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!selectedBusiness) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Please select a business to view personalized deductions.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading your personalized deductions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-deductions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">My Deductions</h1>
          <p className="text-stone-600">Personalized tax deductions for {selectedBusiness.name}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowProfileDialog(true)}
            data-testid="edit-profile-btn"
          >
            {hasProfile ? "Edit Profile" : "Setup Profile"}
          </Button>
          <Button
            onClick={() => setShowCustomDialog(true)}
            className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
            data-testid="add-custom-btn"
          >
            + Add Custom
          </Button>
        </div>
      </div>

      {/* Profile Setup Prompt */}
      {!hasProfile && (
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="p-3 rounded-full bg-amber-100">
                <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Get Personalized Recommendations</h3>
                <p className="text-sm text-amber-700">
                  Complete your business profile to get AI-powered deduction recommendations tailored to your specific situation.
                </p>
              </div>
              <Button
                onClick={() => setShowProfileDialog(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Setup Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1e3a5f]">{deductionsSummary.total_count || 0}</p>
              <p className="text-sm text-stone-600">Tracked Deductions</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">${(deductionsSummary.total_estimated || 0).toLocaleString()}</p>
              <p className="text-sm text-stone-600">Estimated Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{deductionsSummary.claimed_count || 0}</p>
              <p className="text-sm text-stone-600">Claimed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover bg-gradient-to-br from-[#1e3a5f]/5 to-[#2d5a8e]/5">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1e3a5f]">
                ${(taxSavings?.estimated_savings?.total || 0).toLocaleString()}
              </p>
              <p className="text-sm text-[#1e3a5f]">Est. Tax Savings</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deduction Calculators */}
      <DeductionCalculators onAddDeduction={addCalculatorDeduction} />

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="recommended" data-testid="tab-recommended">Recommended</TabsTrigger>
          <TabsTrigger value="tracked" data-testid="tab-tracked">My Deductions</TabsTrigger>
          <TabsTrigger value="savings" data-testid="tab-savings">Tax Savings</TabsTrigger>
        </TabsList>

        {/* Recommended Deductions Tab */}
        <TabsContent value="recommended" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
                Personalized Recommendations
              </CardTitle>
              <CardDescription>
                {recommendations.total_relevant} deductions tailored to your business profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Search recommendations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md"
                data-testid="search-recommendations"
              />

              {filteredRecommendations.length > 0 ? (
                <div className="grid gap-3">
                  {filteredRecommendations.slice(0, 30).map((deduction) => (
                    <div
                      key={deduction.id}
                      className={`p-4 rounded-lg border transition-all ${
                        deduction.is_tracked 
                          ? "bg-[#1e3a5f]/5 border-[#1e3a5f]/20" 
                          : "bg-white border-stone-200 hover:border-[#1e3a5f]/30 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-stone-900">{deduction.name}</h4>
                            {deduction.relevance_score >= 40 && (
                              <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] text-xs">High Match</Badge>
                            )}
                            {deduction.is_tracked && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Tracking</Badge>
                            )}
                          </div>
                          <p className="text-sm text-stone-600 mb-2">{deduction.description}</p>
                          {deduction.relevance_reasons?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {deduction.relevance_reasons.map((reason) => (
                                <Badge key={reason} variant="outline" className="text-xs font-normal">
                                  {reason}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {deduction.category}
                          </Badge>
                          {deduction.limit && (
                            <span className="text-xs text-stone-500">{deduction.limit}</span>
                          )}
                          {!deduction.is_tracked && (
                            <Button
                              size="sm"
                              onClick={() => addDeductionToTracking(deduction)}
                              className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                              data-testid={`add-deduction-${deduction.id}`}
                            >
                              + Track
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  {searchQuery ? "No deductions match your search" : "Complete your profile to see personalized recommendations"}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tracked Deductions Tab */}
        <TabsContent value="tracked" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle>My Tracked Deductions</CardTitle>
                  <CardDescription>
                    {deductionsSummary.total_count || 0} deductions • {deductionsSummary.claimed_count || 0} claimed
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-600">Progress:</span>
                  <Progress 
                    value={deductionsSummary.total_count ? (deductionsSummary.claimed_count / deductionsSummary.total_count) * 100 : 0} 
                    className="w-32"
                  />
                  <span className="text-sm font-medium">
                    {deductionsSummary.total_count ? Math.round((deductionsSummary.claimed_count / deductionsSummary.total_count) * 100) : 0}%
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {trackedDeductions.length > 0 ? (
                <Accordion type="multiple" className="w-full">
                  {/* Group by category */}
                  {Object.entries(
                    trackedDeductions.reduce((acc, d) => {
                      if (!acc[d.category]) acc[d.category] = [];
                      acc[d.category].push(d);
                      return acc;
                    }, {})
                  ).sort().map(([category, deductions]) => (
                    <AccordionItem key={category} value={category}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">{category}</span>
                          <Badge variant="secondary">{deductions.length}</Badge>
                          <span className="text-sm text-stone-500">
                            ${deductions.reduce((sum, d) => sum + (d.actual_amount || d.estimated_amount || 0), 0).toLocaleString()}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pt-2">
                          {deductions.map((deduction) => (
                            <div
                              key={deduction.id}
                              className={`p-4 rounded-lg border ${
                                deduction.is_claimed ? "bg-[#1e3a5f]/5 border-[#1e3a5f]/20" : "bg-white border-stone-200"
                              }`}
                            >
                              <div className="flex flex-col md:flex-row md:items-center gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium text-stone-900">{deduction.name}</h4>
                                    {deduction.is_custom && (
                                      <Badge variant="outline" className="text-xs">Custom</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-stone-600">{deduction.description}</p>
                                  {deduction.notes && (
                                    <p className="text-xs text-stone-500 mt-1 italic">Note: {deduction.notes}</p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <div className="text-right">
                                    <p className="text-sm text-stone-500">Estimated</p>
                                    <p className="font-semibold">${(deduction.estimated_amount || 0).toLocaleString()}</p>
                                  </div>
                                  {deduction.actual_amount > 0 && (
                                    <div className="text-right">
                                      <p className="text-sm text-stone-500">Actual</p>
                                      <p className="font-semibold text-[#1e3a5f]">${deduction.actual_amount.toLocaleString()}</p>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={deduction.is_claimed}
                                      onCheckedChange={(checked) => updateTrackedDeduction(deduction.id, { is_claimed: checked })}
                                      data-testid={`claim-toggle-${deduction.id}`}
                                    />
                                    <span className="text-sm text-stone-600">Claimed</span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => deleteTrackedDeduction(deduction.id)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">No deductions tracked yet</p>
                  <Button onClick={() => setActiveTab("recommended")}>
                    Browse Recommendations
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Savings Tab */}
        <TabsContent value="savings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Savings Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                  </svg>
                  Estimated Tax Savings
                </CardTitle>
                <CardDescription>Based on your tracked deductions for {taxSavings?.tax_year || 2024}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center p-6 rounded-xl bg-gradient-to-br from-[#1e3a5f]/5 to-[#2d5a8e]/5 border border-[#1e3a5f]/15">
                  <p className="text-sm text-[#1e3a5f] mb-1">Total Estimated Savings</p>
                  <p className="text-4xl font-bold text-[#1e3a5f]">
                    ${(taxSavings?.estimated_savings?.total || 0).toLocaleString()}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-xs text-blue-600 mb-1">Federal</p>
                    <p className="text-xl font-semibold text-blue-700">
                      ${(taxSavings?.estimated_savings?.federal || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-blue-500">{taxSavings?.rates_used?.federal || 22}% rate</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-purple-50 border border-purple-100">
                    <p className="text-xs text-purple-600 mb-1">State</p>
                    <p className="text-xl font-semibold text-purple-700">
                      ${(taxSavings?.estimated_savings?.state || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-purple-500">{taxSavings?.rates_used?.state_name || "CA"}</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-amber-50 border border-amber-100">
                    <p className="text-xs text-amber-600 mb-1">Self-Emp</p>
                    <p className="text-xl font-semibold text-amber-700">
                      ${(taxSavings?.estimated_savings?.self_employment || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-amber-500">15.3% SE tax</p>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-stone-50 border border-stone-200">
                  <h4 className="font-medium text-stone-900 mb-2">Summary</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-stone-600">Total Deductions:</span>
                    <span className="font-medium text-right">${(taxSavings?.total_deductions || 0).toLocaleString()}</span>
                    <span className="text-stone-600">Deductions Tracked:</span>
                    <span className="font-medium text-right">{taxSavings?.deduction_count || 0}</span>
                    <span className="text-stone-600">Claimed:</span>
                    <span className="font-medium text-right">{taxSavings?.claimed_count || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Deductions by Category</CardTitle>
                <CardDescription>Breakdown of your tracked deductions</CardDescription>
              </CardHeader>
              <CardContent>
                {taxSavings?.category_breakdown && Object.keys(taxSavings.category_breakdown).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(taxSavings.category_breakdown)
                      .sort((a, b) => b[1].amount - a[1].amount)
                      .map(([category, data]) => (
                        <div key={category} className="p-3 rounded-lg bg-stone-50 border border-stone-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-stone-900">{category}</span>
                            <span className="font-semibold text-[#1e3a5f]">${data.amount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm text-stone-500">
                            <span>{data.count} deduction{data.count !== 1 ? "s" : ""}</span>
                            <span>
                              {taxSavings.total_deductions > 0 
                                ? Math.round((data.amount / taxSavings.total_deductions) * 100) 
                                : 0}% of total
                            </span>
                          </div>
                          <Progress 
                            value={taxSavings.total_deductions > 0 ? (data.amount / taxSavings.total_deductions) * 100 : 0}
                            className="mt-2 h-2"
                          />
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>No deductions tracked yet</p>
                    <p className="text-sm mt-2">Add deductions to see your category breakdown</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recommendations Card */}
            <Card className="lg:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-800">Tax Saving Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(taxSavings?.recommendations || []).map((tip) => (
                    <li key={tip} className="flex gap-2 text-sm text-blue-700">
                      <span className="text-[#1e3a5f] mt-0.5">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Business Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Business Profile Setup</DialogTitle>
            <DialogDescription>
              Tell us about your business to get personalized deduction recommendations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Business Type */}
            <div className="space-y-2">
              <Label>Business Type / Industry</Label>
              <Select
                value={profileForm.business_type}
                onValueChange={(value) => setProfileForm({ ...profileForm, business_type: value })}
              >
                <SelectTrigger data-testid="profile-business-type">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* State */}
            <div className="space-y-2">
              <Label>Business State</Label>
              <Select
                value={profileForm.state}
                onValueChange={(value) => setProfileForm({ ...profileForm, state: value })}
              >
                <SelectTrigger data-testid="profile-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {STATE_OPTIONS.map((state) => (
                    <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Annual Revenue */}
            <div className="space-y-2">
              <Label>Estimated Annual Revenue</Label>
              <Input
                type="number"
                placeholder="e.g., 100000"
                value={profileForm.annual_revenue}
                onChange={(e) => setProfileForm({ ...profileForm, annual_revenue: e.target.value })}
                data-testid="profile-revenue"
              />
            </div>

            {/* Boolean Questions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Do you have employees?</Label>
                <Switch
                  checked={profileForm.has_employees}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_employees: checked })}
                  data-testid="profile-has-employees"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Home office?</Label>
                <Switch
                  checked={profileForm.has_home_office}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_home_office: checked })}
                  data-testid="profile-home-office"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Business vehicle?</Label>
                <Switch
                  checked={profileForm.has_vehicle}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_vehicle: checked })}
                  data-testid="profile-vehicle"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Health insurance?</Label>
                <Switch
                  checked={profileForm.has_health_insurance}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_health_insurance: checked })}
                  data-testid="profile-health-insurance"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Retirement plan?</Label>
                <Switch
                  checked={profileForm.has_retirement_plan}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_retirement_plan: checked })}
                  data-testid="profile-retirement"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Use contractors?</Label>
                <Switch
                  checked={profileForm.uses_contractors}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, uses_contractors: checked })}
                  data-testid="profile-contractors"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Entertainment expenses?</Label>
                <Switch
                  checked={profileForm.entertainment_expenses}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, entertainment_expenses: checked })}
                  data-testid="profile-entertainment"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
                <Label className="text-sm">Maintain inventory?</Label>
                <Switch
                  checked={profileForm.has_inventory}
                  onCheckedChange={(checked) => setProfileForm({ ...profileForm, has_inventory: checked })}
                  data-testid="profile-inventory"
                />
              </div>
            </div>

            {/* Employee Count */}
            {profileForm.has_employees && (
              <div className="space-y-2">
                <Label>Number of Employees</Label>
                <Input
                  type="number"
                  min="1"
                  value={profileForm.employee_count}
                  onChange={(e) => setProfileForm({ ...profileForm, employee_count: parseInt(e.target.value) || 0 })}
                  data-testid="profile-employee-count"
                />
              </div>
            )}

            {/* Vehicle Usage */}
            {profileForm.has_vehicle && (
              <div className="space-y-2">
                <Label>Business Use of Vehicle (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={profileForm.vehicle_usage_percent}
                  onChange={(e) => setProfileForm({ ...profileForm, vehicle_usage_percent: parseInt(e.target.value) || 0 })}
                  data-testid="profile-vehicle-percent"
                />
              </div>
            )}

            {/* Travel Frequency */}
            <div className="space-y-2">
              <Label>Business Travel Frequency</Label>
              <Select
                value={profileForm.travel_frequency}
                onValueChange={(value) => setProfileForm({ ...profileForm, travel_frequency: value })}
              >
                <SelectTrigger data-testid="profile-travel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None / Rarely</SelectItem>
                  <SelectItem value="occasional">Occasional (1-4 trips/year)</SelectItem>
                  <SelectItem value="frequent">Frequent (5+ trips/year)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Cancel</Button>
            <Button onClick={saveProfile} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
              Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Deduction Amount Dialog */}
      <Dialog open={showAmountDialog} onOpenChange={setShowAmountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Deduction</DialogTitle>
            <DialogDescription>
              {selectedDeductionForAmount?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Estimated Amount ($)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={amountForm.estimated}
                onChange={(e) => setAmountForm({ ...amountForm, estimated: e.target.value })}
                data-testid="amount-estimated"
              />
            </div>
            <div className="space-y-2">
              <Label>Actual Amount ($) - Optional</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={amountForm.actual}
                onChange={(e) => setAmountForm({ ...amountForm, actual: e.target.value })}
                data-testid="amount-actual"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any notes about this deduction..."
                value={amountForm.notes}
                onChange={(e) => setAmountForm({ ...amountForm, notes: e.target.value })}
                data-testid="amount-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAmountDialog(false)}>Cancel</Button>
            <Button onClick={confirmAddDeduction} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
              Add Deduction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Deduction Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Deduction</DialogTitle>
            <DialogDescription>
              Add a deduction specific to your business that's not in our library
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Deduction Name *</Label>
              <Input
                placeholder="e.g., Industry-specific equipment"
                value={customDeduction.name}
                onChange={(e) => setCustomDeduction({ ...customDeduction, name: e.target.value })}
                data-testid="custom-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={customDeduction.category}
                onValueChange={(value) => setCustomDeduction({ ...customDeduction, category: value })}
              >
                <SelectTrigger data-testid="custom-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this deduction..."
                value={customDeduction.description}
                onChange={(e) => setCustomDeduction({ ...customDeduction, description: e.target.value })}
                data-testid="custom-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Estimated Amount ($)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={customDeduction.estimated_amount}
                onChange={(e) => setCustomDeduction({ ...customDeduction, estimated_amount: e.target.value })}
                data-testid="custom-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={customDeduction.notes}
                onChange={(e) => setCustomDeduction({ ...customDeduction, notes: e.target.value })}
                data-testid="custom-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomDialog(false)}>Cancel</Button>
            <Button onClick={addCustomDeduction} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
              Add Custom Deduction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDeductions;
